import React from 'react';
import { DT } from '../theme/designTokens';

interface Props {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
  className?: string;
}

export function PrimaryButton({
  children,
  icon,
  onClick,
  disabled,
  loading,
  type = 'button',
  className = '',
}: Props) {
  const isOff = disabled || loading;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isOff}
      className={className}
      style={{
        width:          '100%',
        height:         64,           // touch-first height — preserved from mobile design
        paddingLeft:    24,
        paddingRight:   24,
        borderRadius:   12,           // mobile card radius — preserved (Design Master uses 9999px pill but mobile app preserves 12)
        background:     isOff ? 'rgba(201,168,76,0.25)' : DT.gradient.gold,
        // Design Master source: --shadow-gold = 0 30px 80px -30px #c9a84c73
        boxShadow:      isOff ? 'none' : DT.shadow.gold,
        border:         'none',
        color:          DT.color.goldOnGold,
        fontSize:       16,           // touch-first label size — preserved
        fontWeight:     600,          // Design Master source: btn-gold font-weight: 600
        fontFamily:     DT.font.sans,
        cursor:         isOff ? 'not-allowed' : 'pointer',
        opacity:        isOff ? 0.55 : 1,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            10,
        // Design Master source: btn-gold transition: transform .25s, box-shadow .25s, filter .25s
        transition:     DT.motion.buttonTransition,
        WebkitTapHighlightColor: 'transparent',
        letterSpacing:  '0.2em',     // Design Master source: btn-gold letter-spacing: .2em
        textTransform:  'uppercase', // Design Master source: btn-gold text-transform: uppercase
      }}
      // Design Master source: .btn-gold:hover { filter: brightness(1.08); transform: translateY(-1px) }
      // No hover box-shadow expansion in Design Master.
      onMouseEnter={e => { if (!isOff) { const b = e.currentTarget as HTMLButtonElement; b.style.filter = DT.hover.buttonFilter; b.style.transform = DT.hover.buttonTransform; } }}
      onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.filter = ''; b.style.transform = ''; }}
    >
      {icon && <span style={{ flexShrink: 0, display: 'flex' }}>{icon}</span>}
      {loading ? (
        <span
          style={{
            width:           18,
            height:          18,
            borderRadius:    '50%',
            border:          `2px solid rgba(26,19,5,0.30)`,
            borderTopColor:  DT.color.goldOnGold,
            display:         'inline-block',
            animation:       'spin 0.75s linear infinite',
          }}
        />
      ) : (
        children
      )}
    </button>
  );
}
